import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Target, Calendar, Settings, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePageAnimation } from '../lib/usePageAnimation';
import { useApp } from '../context';
import ContactAvatar from './ContactAvatar';
import { formatInteractionType } from '../lib/utils';
import { interactionsApi, ApiInteraction } from '../lib/api';
import { Interaction } from '../types';
import { registerCacheCleanup, hasChanged } from '../lib/moduleCache';

function mapApiInteraction(i: ApiInteraction): Interaction {
  return {
    id: String(i.id),
    contactId: String(i.contactId),
    type: i.interactionType as Interaction['type'],
    date: i.interactionDate?.split('T')[0] || '',
    notes: i.notes,
    rating: i.rating as Interaction['rating'],
    attachments: i.attachments && i.attachments.length > 0 ? i.attachments : undefined,
  };
}

function getWeekStart(): Date {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const start = new Date(now);
  start.setDate(now.getDate() - daysSinceMonday);
  start.setHours(4, 0, 0, 0); // Monday 4 AM reset (matches Dashboard)
  return start;
}

// Module-level cache so re-mounts don't flash loading spinner
let _weeklyInteractionsCache: Interaction[] | null = null;
registerCacheCleanup(() => {
  _weeklyInteractionsCache = null;
});

export default function WeeklyGoal() {
  const navigate = useNavigate();
  const shouldAnimate = usePageAnimation();
  const { state, setWeeklyGoalTarget } = useApp();
  const { contacts, weeklyGoalTarget } = state;
  const isAuth = !!state.user && !state.user.isGuest;

  const [weeklyInteractions, setWeeklyInteractions] = useState<Interaction[]>(
    _weeklyInteractionsCache || [],
  );
  const [loading, setLoading] = useState(isAuth && !_weeklyInteractionsCache);

  useEffect(() => {
    if (!isAuth) {
      // Guest mode: filter from local state
      const start = getWeekStart();
      const filtered = state.interactions.filter((i) => {
        const iDate = new Date(i.date);
        return iDate >= start;
      });
      setWeeklyInteractions(filtered);
      return;
    }

    // Auth mode: fetch this week's interactions from the API
    const start = getWeekStart();
    const fromDate = start.toISOString().split('T')[0];
    interactionsApi
      .list({ from: fromDate, limit: 100 })
      .then((res) => {
        const mapped = res.data.map(mapApiInteraction);
        if (hasChanged(_weeklyInteractionsCache, mapped)) setWeeklyInteractions(mapped);
        _weeklyInteractionsCache = mapped;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuth, state.interactions]);

  const target = weeklyGoalTarget || 5;
  const progress = Math.min(weeklyInteractions.length, target);
  const percentage = (progress / target) * 100;

  const handleTargetChange = (newTarget: number) => {
    if (newTarget >= 1 && newTarget <= 50) {
      setWeeklyGoalTarget(newTarget);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f5f5] pb-32">
      {/* Header */}
      <header className="p-5 flex items-center justify-between sticky top-0 bg-[#f8f5f5]/80 backdrop-blur-md z-20">
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-xl font-bold text-slate-900">Weekly Goal</h1>
        <div className="size-10" /> {/* Spacer */}
      </header>

      <div className="px-5 space-y-5">
        {/* Progress Card */}
        <motion.div
          initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl"
        >
          <div className="relative z-10 flex flex-col items-center text-center">
            <div className="bg-blue-500/20 p-4 rounded-[2rem] mb-4">
              <Target size={40} className="text-blue-400" />
            </div>
            <h2 className="text-4xl font-bold mb-1">
              {progress} <span className="text-slate-500 text-2xl">/ {target}</span>
            </h2>
            <p className="text-slate-400 text-sm font-medium">Interactions this week</p>

            <div className="w-full mt-8 space-y-2">
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <motion.div
                  initial={shouldAnimate ? { width: 0 } : false}
                  animate={{ width: `${percentage}%` }}
                  className="bg-blue-500 h-full rounded-full shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                />
              </div>
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-slate-500">
                <span>Progress</span>
                <span>{Math.round(percentage)}%</span>
              </div>
            </div>
          </div>
          {/* Decorative Background */}
          <div className="absolute -right-12 -top-12 size-48 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -left-12 -bottom-12 size-48 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Adjust Goal Section */}
        <motion.div
          initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={shouldAnimate ? { delay: 0.1 } : undefined}
          className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-amber-50 p-2 rounded-xl text-amber-500">
              <Settings size={20} />
            </div>
            <h3 className="font-bold text-slate-900">Adjust Weekly Target</h3>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-500">Target Interactions</span>
              <span className="text-2xl font-bold text-slate-900">{weeklyGoalTarget}</span>
            </div>
            <input
              type="range"
              min="1"
              max="21"
              step="1"
              value={weeklyGoalTarget}
              onChange={(e) => handleTargetChange(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              <span>1 / week</span>
              <span>21 / week</span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed text-center">
              Setting a realistic goal helps you stay consistent without feeling overwhelmed.
            </p>
          </div>
        </motion.div>

        {/* Activity Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-lg font-bold text-slate-900">Activity</h3>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
              {weeklyInteractions.length} Total
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={28} className="animate-spin text-slate-400" />
            </div>
          ) : weeklyInteractions.length === 0 ? (
            <div className="bg-white rounded-[2.5rem] p-12 text-center border border-dashed border-slate-200">
              <div className="size-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                <Calendar size={32} />
              </div>
              <p className="text-slate-500 font-medium">No interactions yet this week.</p>
              <p className="text-xs text-slate-400 mt-1">Time to reach out to someone!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {weeklyInteractions.map((interaction, idx) => {
                const contact = contacts.find((c) => c.id === interaction.contactId);
                return (
                  <motion.div
                    key={interaction.id}
                    initial={shouldAnimate ? { opacity: 0, x: -20 } : false}
                    animate={{ opacity: 1, x: 0 }}
                    transition={shouldAnimate ? { delay: 0.2 + idx * 0.05 } : undefined}
                    className="bg-white p-4 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-4"
                  >
                    <ContactAvatar
                      photoUrl={contact?.photoUrl}
                      name={contact?.name || ''}
                      size="md"
                      className="rounded-2xl! shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-slate-900 truncate">{contact?.name}</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 uppercase">
                          {formatInteractionType(interaction.type)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 truncate">
                        {interaction.notes || 'No notes added'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                        {new Date(interaction.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                        })}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
